import { 
  LayoutDashboard, 
  CreditCard, 
  Receipt, 
  Repeat, 
  TrendingUp, 
  Settings, 
  FileText,
  Link as LinkIcon,
  LogOut,
  Users,
  Shield,
  UserCircle,
  Mail,
  Building2,
  Crown,
  Bell,
  Wallet,
  Banknote
} from 'lucide-react';
import { NavLink } from '@/components/NavLink';
import { useAuth } from '@/contexts/AuthContext';
import { useBranding } from '@/contexts/BrandingContext';
import { useTenantSlug } from '@/hooks/useTenantSlug';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from '@/components/ui/sidebar';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const mainItems = [
  { title: 'Dashboard', url: '/dashboard', icon: LayoutDashboard },
  { title: 'Merchants', url: '/merchants', icon: LinkIcon, roles: ['super_user', 'admin'] },
  { title: 'Customers', url: '/customers', icon: UserCircle },
  { title: 'Payments', url: '/payments', icon: CreditCard, roles: ['super_user', 'admin', 'full_user'] },
  { title: 'Invoices', url: '/invoices', icon: FileText, roles: ['super_user', 'admin', 'full_user'] },
  { title: 'Internal Subs', url: '/internal-subscriptions', icon: Repeat },
  { title: 'Transactions', url: '/transactions', icon: Receipt },
  { title: 'Debts', url: '/debts', icon: Wallet },
  { title: 'Reconciliation', url: '/reconciliation', icon: Banknote, roles: ['super_user', 'admin', 'full_user'] },
  { title: 'Analytics', url: '/analytics', icon: TrendingUp },
  { title: 'Subscriptions', url: '/subscriptions', icon: Repeat },
  { title: 'Notifications', url: '/notifications', icon: Bell },
];

const settingsItems = [
  { title: 'Settings', url: '/settings', icon: Settings },
  { title: 'Email Templates', url: '/templates', icon: Mail },
  { title: 'Audit Logs', url: '/audit-logs', icon: FileText, roles: ['super_user', 'admin'] },
  { title: 'Tenants', url: '/tenants', icon: Building2, roles: ['super_user'] },
  { title: 'Subscriptions', url: '/subscription-management', icon: Crown, roles: ['super_user'] },
  { title: 'Admin Panel', url: '/super-admin/users', icon: Shield, roles: ['super_user'] },
  { title: 'User Management', url: '/admin/users', icon: Users, roles: ['super_user', 'admin'] },
];

const getRoleLabel = (role: string | null) => {
  switch (role) {
    case 'super_user': return 'Super User';
    case 'admin': return 'Admin';
    case 'full_user': return 'Full User';
    case 'view_user': return 'View Only';
    default: return role || 'User';
  }
};

export function AppSidebar() {
  const { state } = useSidebar();
  const { user, userRole, signOut } = useAuth();
  const { branding } = useBranding();
  const { buildPath } = useTenantSlug();
  const collapsed = state === 'collapsed';

  const getInitials = (email: string) => {
    return email.substring(0, 2).toUpperCase();
  };

  const companyName = branding?.company_name || 'Payment Hub';
  const logoUrl = branding?.logo_url;

  const canAccessItem = (item: { roles?: string[] }) => {
    if (!item.roles) return true;
    return userRole && item.roles.includes(userRole);
  };

  // Build tenant-scoped URL for menu items
  const getItemUrl = (url: string) => buildPath(url);

  return (
    <TooltipProvider>
      <Sidebar collapsible="icon" className="z-50">
        <SidebarHeader className={`border-b border-sidebar-border ${collapsed ? 'p-2' : 'p-4'}`}>
          {!collapsed && (
            <div className="flex items-center gap-3">
              {logoUrl ? (
                <div className="w-10 h-10 rounded-lg overflow-hidden shadow-glow flex-shrink-0">
                  <img src={logoUrl} alt={companyName} className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="w-10 h-10 rounded-lg bg-gradient-primary flex items-center justify-center shadow-glow">
                  <CreditCard className="w-5 h-5 text-primary-foreground" />
                </div>
              )}
              <div className="min-w-0">
                <h2 className="font-bold text-sidebar-foreground truncate">{companyName}</h2>
                <p className="text-xs text-muted-foreground">Multi-tenant platform</p>
              </div>
            </div>
          )}
          {collapsed && (
            logoUrl ? (
              <div className="w-10 h-10 rounded-lg overflow-hidden shadow-glow mx-auto">
                <img src={logoUrl} alt={companyName} className="w-full h-full object-cover" />
              </div>
            ) : (
              <div className="w-10 h-10 rounded-lg bg-gradient-primary flex items-center justify-center shadow-glow mx-auto">
                <CreditCard className="w-5 h-5 text-primary-foreground" />
              </div>
            )
          )}
        </SidebarHeader>

        <SidebarContent>
          <SidebarGroup>
            {!collapsed && <SidebarGroupLabel>Main Menu</SidebarGroupLabel>}
            <SidebarGroupContent>
              <SidebarMenu>
                {mainItems.filter(canAccessItem).map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                      <SidebarMenuButton asChild>
                        <NavLink
                          to={getItemUrl(item.url)}
                          end
                          className={`flex items-center gap-3 rounded-lg transition-colors hover:bg-sidebar-accent ${collapsed ? 'justify-center px-2 py-2' : 'px-3 py-2'}`}
                          activeClassName="bg-sidebar-accent text-primary font-medium"
                        >
                          <item.icon className="w-4 h-4 flex-shrink-0" />
                          {!collapsed && <span>{item.title}</span>}
                        </NavLink>
                      </SidebarMenuButton>
                      </TooltipTrigger>
                      {collapsed && (
                        <TooltipContent side="right">
                          <p>{item.title}</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          <SidebarGroup>
            {!collapsed && <SidebarGroupLabel>Other</SidebarGroupLabel>}
            <SidebarGroupContent>
              <SidebarMenu>
                {settingsItems.filter(canAccessItem).map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                      <SidebarMenuButton asChild>
                        <NavLink
                          to={getItemUrl(item.url)}
                          className={`flex items-center gap-3 rounded-lg transition-colors hover:bg-sidebar-accent ${collapsed ? 'justify-center px-2 py-2' : 'px-3 py-2'}`}
                          activeClassName="bg-sidebar-accent text-primary font-medium"
                        >
                          <item.icon className="w-4 h-4 flex-shrink-0" />
                          {!collapsed && <span>{item.title}</span>}
                        </NavLink>
                      </SidebarMenuButton>
                      </TooltipTrigger>
                      {collapsed && (
                        <TooltipContent side="right">
                          <p>{item.title}</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter className="border-t border-sidebar-border p-4">
          {!collapsed ? (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Avatar className="w-8 h-8">
                  <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                    {user?.email ? getInitials(user.email) : 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-sidebar-foreground truncate">
                    {user?.email}
                  </p>
                  {userRole && (
                    <Badge variant="secondary" className="text-xs mt-1">
                      {getRoleLabel(userRole)}
                    </Badge>
                  )}
                </div>
              </div>
              <Button
                onClick={signOut}
                variant="ghost"
                size="sm"
                className="w-full justify-start gap-2"
              >
                <LogOut className="w-4 h-4" />
                Sign Out
              </Button>
            </div>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={signOut}
                  variant="ghost"
                  size="icon"
                  className="w-full"
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>Sign Out</p>
              </TooltipContent>
            </Tooltip>
          )}
        </SidebarFooter>
      </Sidebar>
    </TooltipProvider>
  );
}
